import React from "react";
import { Paper, Stack, Typography, Box } from "@mui/material";
import PsychologyIcon from "@mui/icons-material/Psychology";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import PeopleIcon from "@mui/icons-material/People";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import FiberNewIcon from "@mui/icons-material/FiberNew";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import CMKpiCard from "../CareManager/CMKpiCard";
import InfoButton from "../Clinical/InfoButton";

/**
 * Panel 1: BH Population Overview — 6 KPI cards.
 * Total BH, SPMI, Active CM, Unmanaged, BH ER, New Members.
 */
export default function PanelBHOverview({ data, loading, helpContent }) {
  const cards = [
    {
      label: "Total BH Patients",
      value: data?.totalBHPatients,
      subtitle: data ? `${data.genderSplit?.F || 0}F / ${data.genderSplit?.M || 0}M -- Avg age: ${data.avgAge || 0}` : "",
      icon: <PsychologyIcon sx={{ fontSize: 18 }} />,
      color: "#2563EB",
      bgColor: "#EFF6FF",
    },
    {
      label: "SPMI Patients",
      value: data?.spmiPatients,
      subtitle: data?.totalBHPatients ? `${((data.spmiPatients / data.totalBHPatients) * 100).toFixed(1)}% of BH population` : "",
      icon: <WarningAmberIcon sx={{ fontSize: 18 }} />,
      color: "#DC2626",
      bgColor: "#FEE2E2",
    },
    {
      label: "In Active CM",
      value: data?.inActiveCM,
      subtitle: data?.cmBreakdown ? `Ref: ${data.cmBreakdown.referred}, Eng: ${data.cmBreakdown.engaged}, Att: ${data.cmBreakdown.attempted}` : "",
      icon: <PeopleIcon sx={{ fontSize: 18 }} />,
      color: "#2563EB",
      bgColor: "#EFF6FF",
    },
    {
      label: "Unmanaged (no CM)",
      value: data?.unmanagedNoCM,
      subtitle: data?.unmanagedPct != null ? `${data.unmanagedPct}% without care management` : "",
      icon: <ReportProblemIcon sx={{ fontSize: 18 }} />,
      color: data?.unmanagedNoCM > 0 ? "#DC2626" : "#16A34A",
      bgColor: data?.unmanagedNoCM > 0 ? "#FEE2E2" : "#DCFCE7",
    },
    {
      label: "BH ER Events",
      value: data?.bhERPatients,
      subtitle: data ? `${data.totalBHEREvents || 0} total events -- ${data.bhERFrequent2Plus || 0} patients with 2+` : "",
      icon: <LocalHospitalIcon sx={{ fontSize: 18 }} />,
      color: "#D97706",
      bgColor: "#FEF3C7",
    },
    {
      label: "New BH Members",
      value: data?.newBHMembers,
      subtitle: "Joined this enrollment period",
      icon: <FiberNewIcon sx={{ fontSize: 18 }} />,
      color: "#0891B2",
      bgColor: "#ECFEFF",
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider" }}
    >
      <Stack spacing={2}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: 1.5,
              bgcolor: "#F3E8FF",
              color: "#7C3AED",
            }}
          >
            <PsychologyIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>
              BH Population Overview
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Behavioral health patient population summary
            </Typography>
          </Box>
          <InfoButton helpContent={helpContent} />
        </Stack>

        {/* KPI Grid: 3 columns x 2 rows */}
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
          }}
        >
          {cards.map((card) => (
            <CMKpiCard key={card.label} {...card} loading={loading} />
          ))}
        </Box>
      </Stack>
    </Paper>
  );
}
